import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, navigate, usePath } from '../router';
import { useSession } from '../session';
import { api } from '../api';

/** The frame every signed-in screen sits in.
 *
 *  Controls are text, as everywhere else in the brand: the current section is
 *  full ink, the rest sit at 62%.
 *
 *  On a phone the bar cannot hold five controls and a name — it overflowed the
 *  moment a third section appeared — so below the breakpoint it collapses into
 *  the two-bar burger and full-screen dialog the site uses. */
export function Shell({ children, width = 'wide' }: { children: ReactNode; width?: 'wide' | 'narrow' }) {
  const { t, user, theme, setTheme, setUser } = useSession();
  const path = usePath();
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  // showModal gives focus trapping and Escape from the platform rather than
  // from us.
  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
    navigate('/signin');
  };

  const current = (prefix: string) =>
    path === prefix || path.startsWith(prefix + '/') ? 'true' : undefined;

  const sections = (
    <>
      <Link to="/projects" aria-current={current('/projects')} onClick={() => setOpen(false)}>
        {t.projects}
      </Link>
      {user?.isStaff && (
        <>
          <Link to="/time" aria-current={current('/time')} onClick={() => setOpen(false)}>
            {t.time}
          </Link>
          <Link to="/clients" aria-current={current('/clients')} onClick={() => setOpen(false)}>
            {t.clients}
          </Link>
          <Link to="/billing" aria-current={current('/billing')} onClick={() => setOpen(false)}>
            {t.billing}
          </Link>
          <Link to="/people" aria-current={current('/people')} onClick={() => setOpen(false)}>
            {t.users}
          </Link>
        </>
      )}
    </>
  );

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/profile" className="brand" aria-label="Omibee">
            <img src="/logo.svg" alt="" />
          </Link>
          <nav className="sections">{sections}</nav>
          <span className="spacer" />
          <nav className="account">
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={t.theme}
            >
              {theme === 'dark' ? t.light : t.dark}
            </button>
            <Link to="/profile" aria-current={current('/profile')}>{user?.name ?? t.profile}</Link>
            <button onClick={signOut}>{t.signOut}</button>
          </nav>

          <button
            className="burger"
            aria-expanded={open}
            aria-label={t.menu}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </header>

      <dialog ref={dialog} className="menu" onClose={() => setOpen(false)}>
        <button className="burger open" aria-label={t.close} onClick={() => setOpen(false)}>
          <span />
          <span />
        </button>
        <nav>
          {sections}
          <Link to="/profile" onClick={() => setOpen(false)}>{user?.name ?? t.profile}</Link>
          <Link to="/settings" onClick={() => setOpen(false)}>{t.settings}</Link>
          <button onClick={() => { setOpen(false); void signOut(); }}>{t.signOut}</button>
        </nav>
      </dialog>

      <main>
        <div className={width === 'narrow' ? 'col narrow' : 'col'}>{children}</div>
      </main>
    </div>
  );
}
